import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { getUser } from '@/utils/auth';
import { Plus, X, Calendar, Edit2, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Format Date -> 'YYYY-MM-DD' pakai komponen tanggal lokal (bukan toISOString,
// yang bisa geser sehari kalau timezone browser di belakang UTC).
function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultDateRange() {
  const today = new Date();
  const lastWeek = new Date();
  lastWeek.setDate(today.getDate() - 7);
  return {
    fromDate: toDateInputValue(lastWeek),
    toDate: toDateInputValue(today),
  };
}

function emptyFormData() {
  return {
    date: toDateInputValue(new Date()),
    hours_worked: '',
    task_description: '',
  };
}

export default function Timesheet() {
  const user = getUser();
  const canFilterByEmployee = user?.role === 'admin' || user?.role === 'security_manager' || user?.has_subordinates;

  const [timesheets, setTimesheets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [{ fromDate, toDate }, setDateRange] = useState(defaultDateRange);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Re-fetch setiap kali filter employee atau date range berubah (filter di server)
  useEffect(() => {
    fetchTimesheets(selectedEmployee, fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, fromDate, toDate]);

  // Ambil daftar employee sekali saja buat isi dropdown filter
  useEffect(() => {
    if (canFilterByEmployee) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTimesheets = async (employeeFilter, from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (employeeFilter && employeeFilter !== 'all') {
        params.user_id = employeeFilter;
      }
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const response = await api.get('/timesheets', { params });
      setTimesheets(response.data);
    } catch (error) {
      toast.error('Gagal memuat timesheet');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/timesheets/employees');
      setEmployees(response.data);
    } catch (error) {
      // Non-fatal: dropdown filter cuma jadi kosong
      console.error('Gagal memuat daftar employee', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/timesheets/${editingId}`, {
          hours_worked: parseFloat(formData.hours_worked),
          task_description: formData.task_description
        });
        toast.success('Timesheet berhasil diperbarui!');
      } else {
        await api.post('/timesheets', {
          ...formData,
          hours_worked: parseFloat(formData.hours_worked)
        });
        toast.success('Timesheet berhasil ditambahkan!');
      }
      setFormData(emptyFormData());
      setShowForm(false);
      setEditingId(null);
      fetchTimesheets(selectedEmployee, fromDate, toDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const canEditDelete = (timesheet) => {
    if (!timesheet) return false;
    // Only employee or admin can edit/delete own timesheets
    if (user?.role !== 'employee' && user?.role !== 'admin') {
      return false;
    }
    // Must own the timesheet
    if (timesheet.user_id !== user?.id) {
      return false;
    }
    // Must be within 30 days
    const created = new Date(timesheet.created_at);
    const now = new Date();
    const daysDiff = Math.floor((now - created) / (1000 * 60 * 60 * 24));
    return daysDiff <= 30;
  };

  const handleEdit = (timesheet) => {
    if (!canEditDelete(timesheet)) {
      toast.error('Tidak bisa edit timesheet lebih dari 30 hari atau bukan milik Anda');
      return;
    }
    setFormData({
      date: timesheet.date,
      hours_worked: timesheet.hours_worked.toString(),
      task_description: timesheet.task_description
    });
    setEditingId(timesheet.id);
    setShowForm(true);
  };

  const handleDelete = async (timesheet) => {
    if (!canEditDelete(timesheet)) {
      toast.error('Tidak bisa hapus timesheet lebih dari 30 hari atau bukan milik Anda');
      return;
    }
    if (!confirm('Yakin hapus timesheet ini?')) return;
    try {
      await api.delete(`/timesheets/${timesheet.id}`);
      toast.success('Timesheet berhasil dihapus!');
      fetchTimesheets(selectedEmployee, fromDate, toDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus timesheet');
    }
  };

  if (loading && timesheets.length === 0 && !showForm) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="pb-2 border-b border-slate-200/70 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="timesheet-page-title">Timesheet</h1>
          <p className="text-muted-foreground text-sm mt-1">Catat jam kerja dan aktivitas Anda</p>
        </div>
        <Button
          onClick={() => {
            if (showForm) {
              setShowForm(false);
              setEditingId(null);
              setFormData(emptyFormData());
            } else {
              setShowForm(true);
            }
          }}
          data-testid="add-timesheet-button"
          className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
        >
          {showForm ? <X className="w-5 h-5 mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
          {showForm ? 'Tutup Form' : 'Tambah Timesheet'}
        </Button>
      </div>

      {showForm ? (
        /* Add Timesheet Form — tabel disembunyikan selama form terbuka, bukan didorong ke bawah */
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="pb-4 border-b border-slate-100 mb-6">
            <h2 className="text-xl font-bold font-heading text-slate-900">{editingId ? 'Edit Timesheet' : 'Tambah Timesheet Baru'}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{editingId ? 'Update jam kerja Anda' : 'Isi detail jam kerja Anda hari ini'}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date" className="text-sm font-medium text-slate-700">Tanggal</Label>
                <div className="relative">
                  <Input
                    id="date"
                    data-testid="timesheet-date-input"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    disabled={!!editingId}
                    className="w-full border-slate-200 rounded-xl text-sm pr-10 focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 disabled:opacity-80"
                  />
                  <Calendar className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hours" className="text-sm font-medium text-slate-700">Jam Kerja</Label>
                <Input
                  id="hours"
                  data-testid="timesheet-hours-input"
                  type="number"
                  step="0.5"
                  min="0"
                  max="24"
                  value={formData.hours_worked}
                  onChange={(e) => setFormData({ ...formData, hours_worked: e.target.value })}
                  required
                  placeholder="8"
                  className="w-full border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-slate-700">Deskripsi Tugas</Label>
              <Textarea
                id="description"
                data-testid="timesheet-description-input"
                value={formData.task_description}
                onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                required
                placeholder="Jelaskan tugas yang dikerjakan..."
                rows={4}
                className="border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="timesheet-submit-button"
                className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99] disabled:opacity-60"
              >
                {submitting ? 'Menyimpan...' : editingId ? 'Update Timesheet' : 'Simpan Timesheet'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData(emptyFormData());
                }}
                className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
              >
                Batal
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        /* Timesheet List */
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="flex justify-between items-center flex-wrap gap-4 pb-5 border-b border-slate-100 mb-6">
            <div>
              <h2 className="text-xl font-bold font-heading text-slate-900" data-testid="timesheet-list-title">Daftar Timesheet</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Rekap seluruh pencatatan jam kerja</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={fromDate}
                  max={toDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, fromDate: e.target.value }))}
                  className="w-[160px] bg-white border-slate-200 rounded-xl text-sm"
                  data-testid="timesheet-from-date-input"
                />
                <span className="text-muted-foreground text-xs font-medium">s/d</span>
                <Input
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, toDate: e.target.value }))}
                  className="w-[160px] bg-white border-slate-200 rounded-xl text-sm"
                  data-testid="timesheet-to-date-input"
                />
              </div>
              {canFilterByEmployee && (
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="w-[200px] bg-white border-slate-200 rounded-xl text-sm" data-testid="timesheet-employee-filter-select">
                    <SelectValue placeholder="Filter Employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Employee</SelectItem>
                    {user?.has_subordinates && (
                      <SelectItem value={user.id}>{user.name}</SelectItem>
                    )}
                    {employees.map((emp) => (
                      <SelectItem key={emp.user_id} value={emp.user_id}>
                        {emp.user_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Tanggal</th>
                  {user?.role !== 'employee' && <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Karyawan</th>}
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Jam Kerja</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Deskripsi</th>
                  {(user?.role === 'employee' || user?.role === 'admin') && <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={(user?.role === 'employee' || user?.role === 'admin') ? 5 : 4} className="py-10 text-center text-muted-foreground text-sm">
                      Memuat...
                    </td>
                  </tr>
                ) : timesheets.length === 0 ? (
                  <tr>
                    <td colSpan={(user?.role === 'employee' || user?.role === 'admin') ? 5 : 4} className="py-10 text-center text-muted-foreground text-sm">
                      Tidak ada timesheet pada rentang tanggal ini
                    </td>
                  </tr>
                ) : (
                  timesheets.map((timesheet, idx) => (
                    <tr
                      key={timesheet.id}
                      data-testid={`timesheet-row-${timesheet.id}`}
                      className={`border-b border-slate-100 last:border-b-0 hover:bg-[#1A3C34]/5 transition-colors ${
                        idx % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'
                      }`}
                    >
                      <td className="py-3 px-4 text-sm text-slate-700">{new Date(timesheet.date).toLocaleDateString('id-ID')}</td>
                      {user?.role !== 'employee' && <td className="py-3 px-4 text-sm font-medium text-slate-800">{timesheet.user_name}</td>}
                      <td className="py-3 px-4 text-sm font-semibold text-[#1A3C34]">{timesheet.hours_worked} jam</td>
                      <td className="py-3 px-4 text-sm max-w-md text-slate-600">{timesheet.task_description}</td>
                      {(user?.role === 'employee' || user?.role === 'admin') && (
                        <td className="py-3 px-4 text-sm text-center space-x-2">
                          {canEditDelete(timesheet) ? (
                            <>
                              <button
                                onClick={() => handleEdit(timesheet)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                                title="Edit"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleDelete(timesheet)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition"
                                title="Hapus"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}